import {
  Button,
  Flex,
  Header,
  Loader,
  MobileStickyButton,
  Tabs,
  Text,
} from '@components';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { getTabData, getTabPath } from './ProductDetail.utils';
import {
  StyledContainer,
  StyledHeaderContainer,
  TabsContainer,
} from './ProductDetail.styles';
import { Lock } from '@assets/svg';
import { useUserQuery } from '@features/api/userSlice';
import { useNavigate, useParams } from 'react-router-dom';
import { useProductDetailsQuery } from '@features/api/productsSlice';
import { Tab } from '@features/api/response/ProductDetails';
import { TabType } from '@features/api/response/tabContent/TabType';
import RestrictedProductTab from '@components/RestrictedProductTab/RestrictedProductTab';
import { useMediaQueries } from '@utils/mediaQueries/mediaQueries';
import {
  InvestorQualificationStage,
  KYCVerificationStatus,
} from '@features/api/response/User';
import { useUserTransactionsQuery } from '@features/api/userPortfolioSlice';
import { useGetInvestmentIntentQuery } from '@features/api/investmentSlice';
import { TransactionStatus } from '@features/api/response/UserTransactions';

const ProductDetail: React.FC = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const { id = '1', tabId } = useParams();
  const { sm: isMobile } = useMediaQueries();
  const { data: user } = useUserQuery();
  const {
    data: productDetails,
    isFetching,
    isLoading,
  } = useProductDetailsQuery({ productId: id ?? '' }, { skip: !id });
  const { data: investmentIntent } = useGetInvestmentIntentQuery(
    {
      productId: id ?? '',
    },
    {
      refetchOnMountOrArgChange: true,
      skip:
        user?.investorQualificationStage !==
        InvestorQualificationStage.CompletedQualified,
    }
  );
  const isUserKYCCompleted =
    user?.kycVerificationStage === KYCVerificationStatus.CompletedVerified ||
    user?.kycVerificationStage ===
      KYCVerificationStatus.ThirdPartyChecksCompletedVerified;

  const { data: transactions } = useUserTransactionsQuery(undefined, {
    refetchOnMountOrArgChange: true,
    skip:
      user?.investorQualificationStage !==
        InvestorQualificationStage.CompletedQualified || !isUserKYCCompleted,
  });

  const [selectedTabId, setSelectedTabId] = useState<string | undefined>(tabId);

  const isProfileComplete =
    user?.investorQualificationStage ===
    InvestorQualificationStage.CompletedQualified;
  const isProfileNotComplete =
    user?.investorQualificationStage ===
    InvestorQualificationStage.AwaitingQuestionnaire;
  const isUnderReview =
    user?.investorQualificationStage ===
    InvestorQualificationStage.QuestionnaireSubmitted;
  const hasSignedTransaction = transactions?.some(
    (x) => x.status !== TransactionStatus.Intent
  );

  useEffect(() => {
    if (!tabId && productDetails?.tabs?.[0].id) {
      setSelectedTabId(productDetails.tabs[0].id);
      navigate(
        getTabPath(
          productDetails.productId.toString(),
          productDetails.tabs[0].id
        ),
        { replace: true }
      );
    }
  }, [navigate, productDetails, tabId]);

  useEffect(() => {
    if (tabId) setSelectedTabId(tabId);
  }, [tabId]);

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    setSelectedTabId(newValue);
  };

  const handleInvestButtonClick = (): void => {
    if (
      user?.personalDetailsLocked ||
      investmentIntent?.id ||
      transactions?.length
    ) {
      navigate(`/investment-subscription/${productDetails?.productId}/amount`, {
        state: {
          progress: 0,
        },
      });
    } else {
      navigate(
        `/investment-get-started/${productDetails?.productId}/subscription-intro`
      );
    }
  };

  const getTabsData = (x: Tab) => {
    const tabData = getTabData(x, intl, navigate, id);

    const isLegalsRestricted =
      !hasSignedTransaction && x.type === TabType.LEGALS;

    if (
      (isProfileNotComplete && x.type !== TabType.OVERVIEW) ||
      isLegalsRestricted
    ) {
      const background = !isMobile
        ? tabData.restrictedTabBackground
        : tabData.restrictedTabBackgroundMobile;
      tabData.component = (
        <RestrictedProductTab
          backgroundImageUrl={background ?? ''}
          text={intl.formatMessage({
            id: !isUnderReview
              ? 'product.completeQuestionnaireToUnlock'
              : 'home.profileUnderReview',
          })}
          button={
            !isUnderReview
              ? {
                  label: intl.formatMessage({
                    id: 'misc.completeQuestionnaire',
                  }),
                  onClick: () => navigate('/questionnaire-intro'),
                  iconLeft: <Lock />,
                }
              : undefined
          }
        />
      );
    }
    if (isLegalsRestricted) {
      const background = !isMobile
        ? tabData.restrictedTabBackground
        : tabData.restrictedTabBackgroundMobile;
      tabData.component = (
        <RestrictedProductTab
          backgroundImageUrl={background ?? ''}
          text={intl.formatMessage({
            id: 'portfolio.investToUnlock',
          })}
          button={
            !isUnderReview
              ? {
                  label: intl.formatMessage({
                    id: 'product.invest',
                  }),
                  onClick: handleInvestButtonClick,
                  iconLeft: <Lock />,
                }
              : undefined
          }
        />
      );
    }

    return tabData;
  };

  const renderTabButton = () => {
    if (isUnderReview || !user) return null;

    if (!isProfileComplete) {
      if (!isMobile) {
        return (
          <Button
            variant='primary'
            color='primary'
            startIcon={<Lock />}
            onClick={() => navigate('/questionnaire-intro')}
          >
            <FormattedMessage id='misc.completeQuestionnaire' />
          </Button>
        );
      } else if (
        productDetails?.tabs.find((x) => x.id === selectedTabId)?.type ===
        TabType.OVERVIEW
      ) {
        return (
          <MobileStickyButton
            variant='primary'
            color='primary'
            startIcon={<Lock />}
            onClick={() => navigate('/questionnaire-intro')}
          >
            <FormattedMessage id='misc.completeQuestionnaire' />
          </MobileStickyButton>
        );
      } else {
        return null;
      }
    } else if (isProfileComplete) {
      return (
        <MobileStickyButton
          variant='primary'
          color='primary'
          onClick={handleInvestButtonClick}
          sx={{
            width: {
              xs: '100%',
              sm: 130,
              lg: 220,
            },
          }}
        >
          <FormattedMessage id='product.invest' />
        </MobileStickyButton>
      );
    }

    return null;
  };

  if (!productDetails && !isLoading && !isFetching) {
    return (
      <Flex height='100%' alignItems='center' justifyContent='center'>
        <Text variant='h4' fontWeight='fontWeightMedium'>
          <FormattedMessage id='product.errorCouldNotLoadProduct' />
        </Text>
      </Flex>
    );
  }

  return (
    <StyledContainer>
      <Loader isLoading={isFetching || isLoading} />
      <StyledHeaderContainer>
        <Header
          user={user}
          title={{
            name: productDetails?.productName,
            to: '/',
          }}
          smallTitle
        />
      </StyledHeaderContainer>
      <TabsContainer>
        <Tabs
          variant='scrollable'
          tabRightComponent={renderTabButton}
          allowScrollButtonsMobile
          tabItem={productDetails?.tabs.map((x) => getTabsData(x)) ?? []}
          onChange={handleChange}
          value={selectedTabId}
        />
      </TabsContainer>
    </StyledContainer>
  );
};

export default ProductDetail;
